
import React, { JSX, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { ArrowTopRightOnSquareIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import { SUPPORT_PATH } from 'lib/paths'
import {
  ShopifyAuthUrl,
  ShopifyShop,
  SHOPIFY_CONNECTION_NEEDS_RECONNECT,
  SHOPIFY_CONNECTION_PCD_NOT_APPROVED,
  SHOPIFY_CONNECTION_UNINSTALLED
} from 'types/api'
import listMyShopifyShopsQuery from 'graphql/listMyShopifyShops.graphql'
import requestShopifyReauthUrlMutation from 'graphql/requestShopifyReauthUrl.graphql'

const isInstallLink = (url: string | null): url is string =>
  url?.startsWith('https://') === true

const isHealthy = (status: string | null | undefined): boolean =>
  status !== SHOPIFY_CONNECTION_NEEDS_RECONNECT &&
  status !== SHOPIFY_CONNECTION_PCD_NOT_APPROVED &&
  status !== SHOPIFY_CONNECTION_UNINSTALLED

const StatusPill = ({ status }: { status: string | null | undefined }): JSX.Element => {
  if (status === SHOPIFY_CONNECTION_NEEDS_RECONNECT) {
    return (
      <span
        data-testid='shopify-needs-reconnect-badge'
        title='Shopify no longer accepts our stored authorization for this store, reconnect to refresh it'
        className='inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400 ring-1 ring-inset ring-amber-500/30'
      >
        <ShieldExclamationIcon className='size-3' aria-hidden='true' />
        Reconnect needed
      </span>
    )
  }
  if (status === SHOPIFY_CONNECTION_PCD_NOT_APPROVED) {
    return (
      <span
        data-testid='shopify-pcd-badge'
        className='inline-flex shrink-0 items-center rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-300 ring-1 ring-inset ring-red-500/30'
      >
        Action needed from us
      </span>
    )
  }
  if (status === SHOPIFY_CONNECTION_UNINSTALLED) {
    return (
      <span
        data-testid='shopify-uninstalled-badge'
        className='inline-flex shrink-0 items-center rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'
      >
        Removed
      </span>
    )
  }
  return (
    <span
      className='inline-flex shrink-0 items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400 ring-1 ring-inset ring-emerald-500/30'
      title='This store is connected and its sales reach your merch boards'
    >
      Connected
    </span>
  )
}

const ShopStateLine = ({
  shop,
  reconnecting,
  onReconnect
}: {
  shop: ShopifyShop
  reconnecting: boolean
  onReconnect: (shop: string) => void
}): JSX.Element | null => {
  const status = shop.connectionStatus ?? null
  if (status === SHOPIFY_CONNECTION_NEEDS_RECONNECT) {
    return (
      <div className='mt-2'>
        <p className='text-xs text-dm-text-secondary'>
          Shopify stopped accepting our stored authorization for this
          store, so sales have paused. Reconnecting takes one approval
          nothing on your store changes.
        </p>
        <button
          type='button'
          onClick={() => onReconnect(shop.shop)}
          disabled={reconnecting}
          data-testid='shopify-reconnect-button'
          className='mt-2 inline-flex items-center gap-1.5 rounded-md bg-amber-500/90 px-3 py-1.5 text-xs font-semibold text-black hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {reconnecting && <LoadingSpinner />}
          Reconnect
        </button>
      </div>
    )
  }
  if (status === SHOPIFY_CONNECTION_PCD_NOT_APPROVED) {
    return (
      <p data-testid='shopify-pcd-line' className='mt-2 text-xs text-dm-text-secondary'>
        Shopify has not yet approved our app to receive this
        store&apos;s order data, so sales cannot flow to your boards yet.
        We are handling the approval there is nothing to retry here,
        and we will let you know when it clears.
      </p>
    )
  }
  if (status === SHOPIFY_CONNECTION_UNINSTALLED) {
    return (
      <p className='mt-2 text-xs text-dm-text-secondary'>
        The app was removed from this store, so its sales no longer reach
        your boards. Ask us for a fresh install link to reconnect it.
      </p>
    )
  }
  return null
}

const ShopifyCardBody = ({
  shops,
  loading,
  loadError,
  installLinkUrl,
  reconnectingShop,
  onReconnect,
  onRetry
}: {
  shops: ShopifyShop[] | null
  loading: boolean
  loadError: string | null
  installLinkUrl: string | null
  reconnectingShop: string | null
  onReconnect: (shop: string) => void
  onRetry: () => void
}): JSX.Element => {
  if (loadError !== null) {
    return (
      <div>
        <ErrorInline>{loadError}</ErrorInline>
        <button
          type='button'
          onClick={onRetry}
          className='mt-3 inline-flex items-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
        >
          Try again
        </button>
      </div>
    )
  }
  if (loading || shops === null) {
    return (
      <div className='flex items-center justify-center py-6'>
        <LoadingSpinner />
      </div>
    )
  }
  if (shops.length === 0) {
    return (
      <div data-testid='shopify-not-connected'>
        <p className='text-sm text-dm-text-secondary'>
          Connect your Shopify store to drive a merch board from live
          sales. We send you a one-click install link you approve it on
          Shopify and you are done. There is nothing to set up inside your
          store, and we never ask you for a password or an API token.
        </p>
        {isInstallLink(installLinkUrl)
          ? (
            <a
              href={installLinkUrl}
              target='_blank'
              rel='noopener noreferrer'
              data-testid='shopify-install-link'
              className='mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-black hover:bg-brand/80'
            >
              Install on Shopify
              <ArrowTopRightOnSquareIcon className='size-4' aria-hidden='true' />
            </a>
            )
          : (
            <div className='mt-3 rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-xs text-dm-text-secondary'>
              Your install link is issued per store.{' '}
              <Link to={SUPPORT_PATH} className='font-medium text-dm-text-primary underline hover:text-white'>
                Ask us for yours
              </Link>{' '}
              and we will send it over the link is only valid for 7 days,
              so we generate it when you are ready to click it.
            </div>
            )}
      </div>
    )
  }
  return (
    <div data-testid='shopify-connected'>
      <ul className='space-y-3'>
        {shops.map((shop) => (
          <li key={shop.shop} className='rounded-md border border-white/10 bg-dm-bg-accent/20 px-3 py-2.5'>
            <div className='flex items-center justify-between gap-3'>
              <span className='truncate font-mono text-sm text-dm-text-primary' title={shop.shop}>
                {shop.shop}
              </span>
              {!isHealthy(shop.connectionStatus) && <StatusPill status={shop.connectionStatus} />}
            </div>
            <p className='mt-1 text-[11px] text-dm-text-accent'>
              {shop.lastWebhookAt !== null
                ? `Last sale seen ${formatLocalDateTime(shop.lastWebhookAt)}`
                : 'No sales seen yet'}
            </p>
            <ShopStateLine
              shop={shop}
              reconnecting={reconnectingShop === shop.shop}
              onReconnect={onReconnect}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

const headerRightFor = (shops: ShopifyShop[] | null): JSX.Element | null => {
  if (shops === null || shops.length === 0) return null
  const broken = shops.find((s) => !isHealthy(s.connectionStatus))
  if (broken !== undefined) return <StatusPill status={broken.connectionStatus} />
  return <StatusPill status={shops[0].connectionStatus} />
}

export const ShopifyConnectCard = ({
  auth0Token,
  installLinkUrl
}: {
  auth0Token: string | undefined
  installLinkUrl?: string | null
}): JSX.Element => {
  const [shops, setShops] = useState<ShopifyShop[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reconnectingShop, setReconnectingShop] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    setLoadError(null)
    try {
      const { shops: fetched } = await graphqlRequest<{ shops: ShopifyShop[] }>(
        listMyShopifyShopsQuery, {}, auth0Token
      )
      setShops([...fetched].sort((a, b) => a.shop.localeCompare(b.shop)))
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load your stores')
      setShops([])
    }
  }, [auth0Token])

  useEffect(() => {
    void load()
  }, [load])

  const handleReconnect = async (shop: string): Promise<void> => {
    if (auth0Token === undefined || reconnectingShop !== null) return
    setReconnectingShop(shop)
    setActionError(null)
    try {
      const { authUrl } = await graphqlRequest<{ authUrl: ShopifyAuthUrl }>(
        requestShopifyReauthUrlMutation, { shop }, auth0Token
      )
      globalThis.location.assign(authUrl.url)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start the Shopify reconnect')
      setReconnectingShop(null)
    }
  }

  const loading = shops === null && loadError === null

  return (
    <section
      data-testid='shopify-connection-card'
      className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'
    >
      <div className='mb-3 flex items-center justify-between gap-3'>
        <h3 className='truncate text-base font-semibold text-dm-text-primary'>Shopify</h3>
        {headerRightFor(shops)}
      </div>

      <ShopifyCardBody
        shops={shops}
        loading={loading}
        loadError={loadError}
        installLinkUrl={installLinkUrl ?? null}
        reconnectingShop={reconnectingShop}
        onReconnect={(shop) => { void handleReconnect(shop) }}
        onRetry={() => { void load() }}
      />

      {actionError !== null && (
        <div className='mt-3'>
          <ErrorInline onDismiss={() => setActionError(null)}>{actionError}</ErrorInline>
        </div>
      )}
    </section>
  )
}
